import { useState, useEffect } from 'react';

/**
 * Custom hook for managing compact view mode
 * Simplified to toggle between normal and compact layouts only
 */
export const useCompactMode = (defaultMode = 'normal') => {
  // Get stored preference or use default
  const getStoredMode = () => {
    try {
      const stored = localStorage.getItem('compactMode');
      // Migrate old modes to new system
      if (stored === 'dense' || stored === 'ultra') {
        return 'compact';
      }
      return stored && ['normal', 'compact'].includes(stored) ? stored : defaultMode;
    } catch (error) {
      console.warn('Failed to read compact mode from localStorage:', error);
      return defaultMode;
    }
  };

  const [compactMode, setCompactMode] = useState(getStoredMode);

  // Save to localStorage when mode changes
  useEffect(() => {
    try {
      localStorage.setItem('compactMode', compactMode);
      
      // Apply global CSS class to body for mode-specific styling
      document.body.classList.remove('compact-normal', 'compact-dense', 'compact-ultra');
      document.body.classList.add(`compact-${compactMode}`);
      
      // Dispatch custom event for components that need to react to mode changes
      window.dispatchEvent(new CustomEvent('compactModeChanged', {
        detail: { mode: compactMode }
      }));
    } catch (error) {
      console.warn('Failed to save compact mode to localStorage:', error);
    }
  }, [compactMode]);

  // Toggle between normal and compact modes
  const toggleCompactMode = () => {
    setCompactMode(prevMode => prevMode === 'normal' ? 'compact' : 'normal');
  };

  // Set specific mode
  const setMode = (mode) => {
    if (['normal', 'compact'].includes(mode)) {
      setCompactMode(mode);
    } else {
      console.warn('Invalid compact mode:', mode, '. Valid modes are: normal, compact');
    }
  };

  // Get CSS classes for current mode
  const getCompactClasses = () => {
    const baseClasses = ['compact-layout'];
    
    if (compactMode === 'compact') {
      return [...baseClasses, 'compact-mode'].join(' ');
    }
    
    return [...baseClasses, 'normal-mode'].join(' ');
  };

  // Get inline styles for current mode - compact is very aggressive
  const getCompactStyles = () => {
    if (compactMode === 'compact') {
      return {
        fontSize: '0.6875rem',
        lineHeight: 1.1,
        padding: '4px',
        margin: '2px',
      };
    }
    
    return {
      fontSize: '0.875rem',
      lineHeight: 1.43,
      padding: '16px',
      margin: '8px',
    };
  };

  // Get table-specific styles - compact is very dense
  const getTableStyles = () => {
    if (compactMode === 'compact') {
      return {
        '& .MuiTableCell-root': {
          padding: '2px 4px',
          fontSize: '0.6875rem',
          lineHeight: 1.1,
          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
        },
        '& .MuiTableCell-head': {
          padding: '4px',
          fontSize: '0.65rem',
          fontWeight: 600,
          lineHeight: 1.1,
        },
        '& .MuiTable-root': {
          '& .MuiTableRow-root': {
            minHeight: '24px',
          },
        },
      };
    }
    
    return {
      '& .MuiTableCell-root': {
        padding: '16px',
        fontSize: '0.875rem',
        lineHeight: 1.43,
      },
      '& .MuiTableCell-head': {
        padding: '16px',
        fontSize: '0.875rem',
        fontWeight: 500,
      },
    };
  };

  // Get button styles - compact is very small
  const getButtonStyles = () => {
    if (compactMode === 'compact') {
      return {
        padding: '2px 8px',
        fontSize: '0.6875rem',
        minHeight: '24px',
        lineHeight: 1.2,
      };
    }
    
    return {
      padding: '6px 16px',
      fontSize: '0.875rem',
      minHeight: '36px',
    };
  };

  // Check if current mode is compact
  const isCompact = compactMode === 'compact';
  const isNormal = compactMode === 'normal';

  return {
    compactMode,
    setCompactMode: setMode,
    toggleCompactMode,
    isCompact,
    isNormal,
    getCompactClasses,
    getCompactStyles,
    getTableStyles,
    getButtonStyles,
  };
};

export default useCompactMode;